import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  REGIONS,
  type AskFacet,
  type LiftedFilter,
  type ReasonTrace,
  type Region,
  type SearchResponse,
  type SearchTarget,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { recordSearchSelection, runSearch } from '../api/search';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { ErrorNotice } from '../components/ErrorNotice';
import { Snippet } from '../components/Snippet';
import { TagInput } from '../components/TagInput';
import { processingLabel } from '../components/trust-labels';
import { useCloudeqEntities } from '../hooks/useCloudeqEntities';
import { requireSurface } from '../routes/surfaces';
import { JudgementControls } from './search/JudgementControls';

/**
 * Search — FR-18, FR-20, FR-25, FR-27, NFR-06, NFR-10, O-6.
 *
 * One scope-filtered query through the API over either contracts (metadata
 * *and* converted content) or the standard clause library.
 *
 * ## Nothing about the ranking happens here
 *
 * No LLM is in this path and none can be added to it: NFR-10 on latency, FR-27
 * because an LLM reranker cannot explain itself, and O-6 because the same query
 * must rank the same way every time. What the browser renders is what the index
 * produced — the `reason_trace`'s `detail` strings verbatim, because they are
 * the compliance artefact and have to read the same in this UI, in a log and in
 * an evidence pack.
 *
 * Snippets likewise: `ContractSearchHitSchema` says they arrive escaped and
 * `<mark>`-marked by the index and *"the front end must not re-derive them"*.
 * `Snippet` splits on those markers rather than setting raw HTML, so the only
 * markup this surface can emit is `<mark>` whatever the payload contains.
 *
 * ## An empty clause search is not "no results"
 *
 * The clause library is empty today — nothing has segmented or embedded — so a
 * clause search legitimately returns zero hits. Reporting that as "no results"
 * would tell the user their query was bad when the corpus is what is missing.
 *
 * ## Ask mode (CLM-97, R-97-1) — the default
 *
 * `target: 'ask'` merges countable facts over the nine key-terms fields with
 * passage-level hybrid hits (falling back to a whole-document lexical match for
 * a contract `contract_clause` has not been segmented for yet — D-97-1(a)).
 * `lifted_filters` is the deterministic intent pass's own account of what it
 * read out of the typed query (D-97-5) — never silent: each one renders as a
 * chip, and removing a chip deletes its `matched_text` from the query box
 * (the only lever the query text gives the rule to fire on) rather than
 * pretending the server can be told "ignore that lift" out of band.
 *
 * ## A hit's pipeline and trust tags (CLM-108, R-108-14)
 *
 * Both hit kinds print `processing_status` through `processingLabel`, whose end
 * state reads "Settled", never the raw "Verified": an auto-approved contract
 * reaches it too. "Unverified fields" still means work is outstanding
 * (`has_unverified_fields`); "Auto-approved fields" is read from `data_trust`
 * alone, so a policy settlement is never shown as nothing to say.
 */

type SearchPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  /** `query` is the text as typed at submit time — what a judgement records (CLM-39), not the alias-expanded `effective_query`. */
  | { readonly kind: 'results'; readonly response: SearchResponse; readonly query: string }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

export function Search() {
  const surface = requireSurface('search');

  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<SearchTarget>('ask');
  /** CLM-77 — the all-of tag filter; a predicate beside the scope, never a rank change (FR-27). Shared by contracts and ask (CLM-97): both read the same SearchNarrow. */
  const [tags, setTags] = useState<readonly string[]>([]);
  /* CLM-87 — the PRIMARY party's region; a predicate beside the tags, never a rank change (FR-27). */
  const [region, setRegion] = useState<'all' | Region>('all');
  /* CLM-91 — the signing cloudEQ entity; a predicate on the contract's own column, never a rank change (FR-27). */
  const [entity, setEntity] = useState<string>('all');
  /** CLM-97 (D-97-5) — set by clicking a `payment_terms` facet value; ask mode only. */
  const [paymentTerms, setPaymentTerms] = useState<string | null>(null);
  /* CLM-99 (D-99-7a) — the primary party's Dynamics billing client id, exact match; a predicate, never a rank change (FR-27). */
  const [billingClientId, setBillingClientId] = useState('');
  const entities = useCloudeqEntities();
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [phase, setPhase] = useState<SearchPhase>({ kind: 'idle' });

  const abortRef = useRef<AbortController | null>(null);

  /**
   * The narrow/hit-fetching half of a search, factored out of the form submit
   * so a facet click or a lifted-filter chip removal can re-run the same query
   * with one field changed, without duplicating the abort/phase machinery.
   * `paymentTermsOverride` lets a facet click narrow immediately, before the
   * `setPaymentTerms` state update has committed.
   */
  function performSearch(q: string, paymentTermsOverride: string | null = paymentTerms) {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase({ kind: 'running' });

    const wantsContractNarrow = target === 'contracts' || target === 'ask';
    const billingClientIdTrimmed = billingClientId.trim();
    const narrow = {
      ...(wantsContractNarrow && tags.length > 0 ? { tags: [...tags] } : {}),
      ...(wantsContractNarrow && region !== 'all' ? { region } : {}),
      ...(wantsContractNarrow && entity !== 'all' ? { cloudeq_entity: entity } : {}),
      ...(wantsContractNarrow && billingClientIdTrimmed !== '' ? { billing_client_id: billingClientIdTrimmed } : {}),
      ...(target === 'ask' && paymentTermsOverride !== null ? { payment_terms: paymentTermsOverride } : {}),
    };

    void (async () => {
      try {
        const response = await runSearch(
          {
            q,
            target,
            include_reason_trace: true,
            ...(Object.keys(narrow).length > 0 ? { narrow } : {}),
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setPhase({ kind: 'results', response, query: q });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    })();
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const q = query.trim();
    if (q === '') {
      // The contract's own rule: `q` is `.trim().min(1)`. Whitespace is not a
      // query, and saying so here saves a round trip.
      setFieldError('Type something to search for. Whitespace is not a query.');
      return;
    }

    setFieldError(null);
    performSearch(q);
  }

  /**
   * A lifted-filter chip's ×. There is no "ignore this lift" request field —
   * the intent pass re-derives its filters from `q` on every search (pure,
   * deterministic, O-6), so the only way to stop a rule firing again is to
   * remove the text that triggers it. `matched_text` is exactly that text.
   */
  function onRemoveLiftedFilter(filter: LiftedFilter) {
    const index = query.toLowerCase().indexOf(filter.matched_text.toLowerCase());
    if (index === -1) return;
    const next = (query.slice(0, index) + query.slice(index + filter.matched_text.length))
      .replace(/\s+/g, ' ')
      .trim();
    setQuery(next);
    performSearch(next);
  }

  /** A `payment_terms` facet value, clicked to narrow (D-97-5) — the query text is unchanged. */
  function onNarrowPaymentTerms(value: string) {
    setPaymentTerms(value);
    performSearch(query.trim(), value);
  }

  /**
   * Which result the reviewer actually opened — WP-09 §5 stage ⑥, the cheapest
   * relevance signal available. Deliberately not awaited and deliberately
   * swallowed: this is telemetry, and a failure to record it must never stop
   * someone opening a contract.
   */
  function noteSelection(search_id: string, selected: { contract_key?: string; clause_code?: string }) {
    void recordSearchSelection({
      search_id,
      ...(selected.contract_key === undefined ? {} : { selected_contract_key: selected.contract_key }),
      ...(selected.clause_code === undefined ? {} : { selected_clause_code: selected.clause_code }),
    }).catch(() => {
      /* telemetry only — never surfaced, never retried */
    });
  }

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      <form className="card" onSubmit={onSubmit}>
        <h3>Search</h3>
        <p>
          Type how you think, not how a lawyer drafts. Your scope is resolved from your sign-in
          server-side and compiled into the query, so this searches everything you may see and
          nothing else.
        </p>

        <div className="sbar">
          <div className="field grow">
            <label htmlFor="s-q">Query</label>
            <input
              id="s-q"
              type="search"
              autoComplete="off"
              value={query}
              disabled={phase.kind === 'running'}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="s-target">Search over</label>
            <select
              id="s-target"
              value={target}
              disabled={phase.kind === 'running'}
              onChange={(event) => {
                const next = event.target.value;
                setTarget(next === 'clauses' ? 'clauses' : next === 'contracts' ? 'contracts' : 'ask');
                setPaymentTerms(null);
              }}
            >
              <option value="ask">Ask — natural language</option>
              <option value="contracts">Contracts — metadata and content</option>
              <option value="clauses">Standard clause library</option>
            </select>
          </div>
          {target === 'contracts' || target === 'ask' ? (
            <>
              <div className="field">
                <label htmlFor="s-region">Region</label>
                <select
                  id="s-region"
                  value={region}
                  disabled={phase.kind === 'running'}
                  onChange={(event) => {
                    setRegion(event.target.value as 'all' | Region);
                  }}
                >
                  <option value="all">All regions</option>
                  {REGIONS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="s-cloudeq-entity">cloudEQ entity</label>
                <select
                  id="s-cloudeq-entity"
                  value={entity}
                  disabled={phase.kind === 'running'}
                  onChange={(event) => {
                    setEntity(event.target.value);
                  }}
                >
                  <option value="all">All cloudEQ entities</option>
                  {entities.map((row) => (
                    <option key={row.entity_key} value={row.entity_key}>
                      {row.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field grow">
                <label htmlFor="s-tags">Tags (all of)</label>
                <TagInput id="s-tags" mode="filter" value={tags} onChange={setTags} disabled={phase.kind === 'running'} />
              </div>
              <div className="field">
                <label htmlFor="s-billing-client-id">Dynamics billing id</label>
                <input
                  id="s-billing-client-id"
                  type="text"
                  autoComplete="off"
                  value={billingClientId}
                  disabled={phase.kind === 'running'}
                  onChange={(event) => setBillingClientId(event.target.value)}
                />
              </div>
            </>
          ) : null}
          <button className="btn pri" type="submit" disabled={phase.kind === 'running'}>
            {phase.kind === 'running' ? 'Searching…' : 'Search'}
          </button>
        </div>

        {fieldError === null ? null : (
          <p className="ferr" role="alert">
            {fieldError}
          </p>
        )}
      </form>

      {phase.kind === 'running' ? (
        <div className="card" role="status" aria-busy="true">
          <h3>Running the query</h3>
          <p>
            One statement, with your scope as a predicate inside its own <code>WHERE</code> clause.
            No model is involved.
          </p>
        </div>
      ) : null}

      {phase.kind === 'problem' ? (
        <ErrorNotice
          kind={phase.problem}
          detail={phase.detail}
          correlation_id={phase.correlation_id}
        />
      ) : null}

      {phase.kind === 'results' ? (
        <Results
          response={phase.response}
          query={phase.query}
          onSelect={noteSelection}
          onRemoveLiftedFilter={onRemoveLiftedFilter}
          onNarrowPaymentTerms={onNarrowPaymentTerms}
        />
      ) : null}
    </>
  );
}

function Results({
  response,
  query,
  onSelect,
  onRemoveLiftedFilter,
  onNarrowPaymentTerms,
}: {
  readonly response: SearchResponse;
  readonly query: string;
  readonly onSelect: (
    search_id: string,
    selected: { contract_key?: string; clause_code?: string },
  ) => void;
  readonly onRemoveLiftedFilter: (filter: LiftedFilter) => void;
  readonly onNarrowPaymentTerms: (value: string) => void;
}) {
  return (
    <div className="card">
      <h3>Results</h3>
      <p className="sran">
        Ran <code>{response.effective_query}</code> in {response.latency_ms} ms ·{' '}
        {response.page.returned_count} shown
        {response.page.total_in_scope === null
          ? ' · total not counted'
          : ` of ${String(response.page.total_in_scope)} in your scope`}
      </p>

      {response.target === 'ask' ? (
        <>
          <LiftedFilterChips filters={response.lifted_filters} onRemove={onRemoveLiftedFilter} />
          <FacetsPanel facets={response.facets} onNarrowPaymentTerms={onNarrowPaymentTerms} />
        </>
      ) : null}

      {response.hits.length === 0 ? (
        <div className="dt-empty">
          {response.target === 'clauses' ? (
            <>
              <p>
                <strong>No clauses are indexed yet.</strong>
              </p>
              <p>
                The standard clause library is empty — nothing has been segmented or embedded, so
                there is nothing for a clause query to match. This is not a statement about your
                query.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>Nothing in your scope matched.</strong>
              </p>
              <p>
                The query ran against everything you may see. A contract outside your scope is not
                reported as excluded — the statement is incapable of returning it, so there is
                nothing to report about it.
              </p>
            </>
          )}
        </div>
      ) : null}

      <div className="hits">
        {response.target === 'ask'
          ? response.hits.map((hit) => (
              <article className="hit" key={hit.contract.contract_key}>
                <div className="hit-hd">
                  <h3>
                    <Link
                      to={`/contracts/record/${encodeURIComponent(hit.contract.contract_key)}`}
                      onClick={() => {
                        onSelect(response.search_id, { contract_key: hit.contract.contract_key });
                      }}
                    >
                      {hit.contract.contract_key}
                    </Link>
                  </h3>
                  <span className="tag up">{hit.contract.lifecycle_status}</span>
                  <span className="tag acc">{processingLabel(hit.contract.processing_status)}</span>
                  {hit.passage.kind === 'lexical_fallback' ? (
                    <span className="tag acc">Not yet passage-indexed</span>
                  ) : null}
                  {hit.contract.has_unverified_fields ? (
                    <span className="tag bad">Unverified fields</span>
                  ) : null}
                  {hit.contract.data_trust === 'AutoApproved' ? (
                    <span className="tag ai">Auto-approved fields</span>
                  ) : null}
                  {hit.contract.processing_restricted ? (
                    <span className="tag bad">Processing restricted</span>
                  ) : null}
                  {hit.tags.length > 0 ? (
                    <ul className="tags-ro" aria-label={`Tags of ${hit.contract.contract_key}`}>
                      {hit.tags.map((tag) => (
                        <li key={tag} className="tag">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <p className="hit-meta">
                  {hit.contract.primary_party_display_name} ·{' '}
                  <ContractTypeTag type={hit.contract.contract_type} confirmation={hit.contract.contract_type_confirmation} /> ·
                  expires {hit.contract.expiry_date ?? 'not stated'}
                </p>

                <div className="snips">
                  <p className="snip-f">
                    <span className="fname">
                      {hit.passage.kind === 'passage'
                        ? (hit.passage.paragraph_ref ?? 'passage')
                        : 'whole document — not yet passage-indexed'}
                    </span>
                    <Snippet html={hit.passage.html} />
                  </p>
                </div>

                <ReasonTraceDisclosure trace={hit.reason_trace} />
              </article>
            ))
          : response.target === 'contracts'
          ? response.hits.map((hit) => (
              <article className="hit" key={hit.contract.contract_key}>
                <div className="hit-hd">
                  <h3>
                    <Link
                      to={`/contracts/record/${encodeURIComponent(hit.contract.contract_key)}`}
                      onClick={() => {
                        onSelect(response.search_id, { contract_key: hit.contract.contract_key });
                      }}
                    >
                      {hit.contract.contract_key}
                    </Link>
                  </h3>
                  <span className="tag up">{hit.contract.lifecycle_status}</span>
                  <span className="tag acc">{processingLabel(hit.contract.processing_status)}</span>
                  {hit.contract.has_unverified_fields ? (
                    <span className="tag bad">Unverified fields</span>
                  ) : null}
                  {hit.contract.data_trust === 'AutoApproved' ? (
                    <span className="tag ai">Auto-approved fields</span>
                  ) : null}
                  {hit.contract.processing_restricted ? (
                    <span className="tag bad">Processing restricted</span>
                  ) : null}
                  {hit.tags.length > 0 ? (
                    <ul className="tags-ro" aria-label={`Tags of ${hit.contract.contract_key}`}>
                      {hit.tags.map((tag) => (
                        <li key={tag} className="tag">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <p className="hit-meta">
                  {hit.contract.primary_party_display_name} ·{' '}
                  <ContractTypeTag type={hit.contract.contract_type} confirmation={hit.contract.contract_type_confirmation} /> ·
                  expires {hit.contract.expiry_date ?? 'not stated'} ·{' '}
                  {hit.contract.primary_party_billing_client_id === null ? (
                    <span className="mut">Not billable</span>
                  ) : (
                    <>
                      billing id <code>{hit.contract.primary_party_billing_client_id}</code>
                    </>
                  )}
                </p>

                <div className="snips">
                  {hit.snippets.map((snippet, index) => (
                    <p className="snip-f" key={`${snippet.field_name}-${String(index)}`}>
                      <span className="fname">{snippet.field_name}</span>
                      <Snippet html={snippet.html} />
                    </p>
                  ))}
                </div>

                <ReasonTraceDisclosure trace={hit.reason_trace} />
              </article>
            ))
          : response.hits.map((hit) => (
              <article className="hit" key={hit.clause.clause_code}>
                <div className="hit-hd">
                  <h3>
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => {
                        onSelect(response.search_id, { clause_code: hit.clause.clause_code });
                      }}
                    >
                      {hit.clause.clause_code} — {hit.clause.title}
                    </button>
                  </h3>
                  <span className="tag out">v{hit.clause.version_no}</span>
                  {hit.clause.is_active ? null : <span className="tag bad">Inactive</span>}
                </div>
                <p className="hit-meta">
                  {hit.clause.category ?? 'no category'} · used by{' '}
                  {hit.in_scope_usage_count} contract
                  {hit.in_scope_usage_count === 1 ? '' : 's'} in your scope
                </p>
                <JudgementControls search_id={response.search_id} query_text={query} hit={hit} />
                <ReasonTraceDisclosure trace={hit.reason_trace} />
              </article>
            ))}
      </div>
    </div>
  );
}

/** One line per `LiftedFilter` kind — the same wording wherever a chip renders. */
function liftedFilterLabel(filter: LiftedFilter): string {
  switch (filter.kind) {
    case 'party':
      return `Party: ${filter.display_name}`;
    case 'payment_terms':
      return `Payment terms: ${filter.value}`;
    case 'contract_type':
      return `Contract type: ${filter.value}`;
    case 'date_window':
      return `${filter.field === 'expiring' ? 'Expiring' : 'Effective'} ${filter.from ?? '…'} to ${filter.to ?? '…'}`;
  }
}

/**
 * D-97-5 — what the deterministic intent pass read out of the typed query,
 * shown as editable/removable chips (CLAUDE.md rule 14: a rule the user can see
 * and override, never a silent filter). See `onRemoveLiftedFilter`'s own note
 * for why "remove" means deleting the triggering text, not a request flag.
 */
function LiftedFilterChips({
  filters,
  onRemove,
}: {
  readonly filters: readonly LiftedFilter[];
  readonly onRemove: (filter: LiftedFilter) => void;
}) {
  if (filters.length === 0) return null;
  return (
    <ul className="chips" aria-label="Filters detected in your query">
      {filters.map((filter, index) => {
        const label = liftedFilterLabel(filter);
        return (
          <li key={`${filter.kind}-${String(index)}`} className="chip">
            {label}
            <button type="button" className="chip-x" aria-label={`Remove detected filter: ${label}`} onClick={() => onRemove(filter)}>
              ×
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What follows a facet value's count. A value every contributing row of which
 * a person settled reads bare; the rest say why not. CLM-108 adds
 * `auto_approved` — every contributing row settled by policy — which must
 * never read bare, or a policy settlement would pass for a person's (CR-06).
 * A `Record`, so a fifth state fails to compile rather than printing its raw key.
 */
const FACET_STATE_SUFFIX: Readonly<Record<AskFacet['values'][number]['verification_state'], string | null>> = {
  verified: null,
  unverified: ' (unverified)',
  mixed: ' (mixed)',
  auto_approved: ' (auto-approved)',
};

/**
 * D-97-4 — the countable-facts header. Only `payment_terms` has a narrow field
 * on the wire (`SearchNarrowSchema.payment_terms`), so it is the only facet
 * whose values are clickable; the other eight are shown for information, same
 * as the ticket's own examples ("how much insurance for MCD") — a fact to read,
 * not necessarily a filter to apply.
 */
function FacetsPanel({
  facets,
  onNarrowPaymentTerms,
}: {
  readonly facets: readonly AskFacet[];
  readonly onNarrowPaymentTerms: (value: string) => void;
}) {
  const withValues = facets.filter((facet) => facet.values.length > 0);
  if (withValues.length === 0) return null;
  return (
    <div className="facets">
      <h4>Countable facts in your scope</h4>
      {withValues.map((facet) => (
        <p className="facet" key={facet.field_name}>
          <span className="fname">{facet.field_name.replace(/_/g, ' ')}</span>{' '}
          {facet.values.map((value, index) => (
            <span key={value.value}>
              {index === 0 ? null : '; '}
              {facet.field_name === 'payment_terms' ? (
                <button type="button" className="btn sm ghost" onClick={() => onNarrowPaymentTerms(value.value)}>
                  {value.value}
                </button>
              ) : (
                value.value
              )}{' '}
              × {value.count}
              {FACET_STATE_SUFFIX[value.verification_state]}
            </span>
          ))}
        </p>
      ))}
    </div>
  );
}

/**
 * FR-27 — why this result scored what it scored.
 *
 * Each contribution's `detail` is generated by the search service and printed
 * verbatim. It is not composed, re-worded or summarised here: it is the
 * compliance artefact, and it has to read identically wherever it appears.
 */
function ReasonTraceDisclosure({ trace }: { readonly trace: ReasonTrace | null }) {
  if (trace === null) {
    return <p className="mut">No reason trace was returned for this result.</p>;
  }

  return (
    <details className="trace">
      <summary>
        Why this ranked here — score {trace.final_score.toFixed(4)}, base{' '}
        {trace.base_score.toFixed(4)}
      </summary>
      <ol>
        {trace.contributions.map((contribution, index) => (
          <li key={`${contribution.source}-${String(index)}`}>
            <span className="src">{contribution.source}</span>
            {contribution.detail}{' '}
            <span className="val">({contribution.value.toFixed(4)})</span>
          </li>
        ))}
      </ol>
      {trace.aliases_applied.length === 0 ? null : (
        <p className="mut">
          Aliases applied before retrieval:{' '}
          {trace.aliases_applied
            .map((alias) => `${alias.alias_term} → ${alias.canonical_term}`)
            .join('; ')}
        </p>
      )}
    </details>
  );
}
